import { Instance, SnapshotIn, types, flow, getEnv } from 'mobx-state-tree';
import { DataState, DataStateStore } from '@ace/core';

import { IStoresEnv } from '@core/storesEnv';
import {
    performRevokeManagedDevice,
    performSearchManagedDevices,
    ManagedPairedDevice,
    PagedMeta,
    PairedDeviceSummary,
    PAIRING_STATUS_ACTIVE,
} from 'AuthPart/DevicePairing/services';

export const MANAGED_DEVICES_PAGE_SIZE = 10;

/**
 * How often the panel re-reads the list while somebody is looking at it.
 *
 * Devices get paired by other people — that is the whole point of the on-behalf flow — so a panel
 * that only loads once is wrong within seconds of being opened. Twenty seconds is short enough
 * that a device someone just paired shows up while they are still standing there, and long enough
 * that leaving the tab open all afternoon is not a meaningful load.
 */
export const MANAGED_DEVICES_POLL_INTERVAL_MS = 20000;

const emptyMeta = (): PagedMeta => ({
    totalEntities: 0,
    totalPages: 0,
    currentPage: 0,
    limit: MANAGED_DEVICES_PAGE_SIZE,
    isFirstPage: true,
    isLastPage: true,
});

/**
 * Every paired device in the company, for an administrator or manager.
 *
 * Separate from DevicePairingUIStore, which is about one person's own devices and the dialog that
 * pairs them. The two answer different questions and are never on screen together.
 */
export const ManagedDevicesUIStoreInferred = types
    .model('ManagedDevicesUIStoreInferred', {
        searchText: types.optional(types.string, ''),
        page: types.optional(types.number, 0),
        sortFieldName: types.optional(types.string, ''),
        sortDirection: types.optional(types.number, 1),
        deviceToRevoke: types.optional(types.string, ''),
        // Live pairings only until somebody widens it, which is the honest default: the panel is
        // mostly asked "what is signed in right now", and history would bury that.
        statuses: types.optional(types.array(types.string), () => [PAIRING_STATUS_ACTIVE]),
    })
    .volatile(() => ({
        status: DataStateStore.create({ state: DataState.initial }),
        revokeStatus: DataStateStore.create({ state: DataState.initial }),
        devices: [] as ManagedPairedDevice[],
        meta: emptyMeta(),
        summary: null as PairedDeviceSummary | null,
        // Told by the server rather than derived from roles here, so there is one place that
        // decides who may look past the live pairings.
        canSeeHistory: false,
        revokeError: '',
        // The table reports its paging/sorting state once when it mounts, which happens after the
        // first load has already put rows on screen. That echo is not a user action and must not
        // cost a second request.
        ignoreNextQuery: false,
        pollHandle: undefined as ReturnType<typeof setInterval> | undefined,
    }))
    .actions(self => {
        const { api } = getEnv<IStoresEnv>(self);

        /**
         * @param silent A background refresh: same query, no loading state, and a failure leaves
         * what is on screen alone. Blanking the table because one poll timed out would be worse
         * than showing rows a few seconds out of date, and a skeleton every twenty seconds makes
         * the panel unusable to read.
         */
        const fetchDevices = flow(function* (silent: boolean) {
            if (!silent) {
                self.status.setLoading();
            }

            const result = yield performSearchManagedDevices(api, {
                payload: {
                    searchText: self.searchText,
                    limit: MANAGED_DEVICES_PAGE_SIZE,
                    page: self.page,
                    statuses: self.statuses.slice(),
                    sortFieldName: self.sortFieldName || undefined,
                    sortDirection: self.sortFieldName ? self.sortDirection : undefined,
                },
                errorHandlers: { DEFAULT: '' },
                disableDefaultErrorHandlers: true,
            });

            if (result.success) {
                self.devices = result.data?.entities || [];
                self.meta = result.data?.meta || emptyMeta();
                self.summary = result.data?.summary || null;
                self.canSeeHistory = !!result.data?.canSeeHistory;
                self.status.setDone();
            } else if (!silent) {
                self.devices = [];
                self.meta = emptyMeta();
                self.status.setError();
            }
        });

        const load = () => fetchDevices(false);

        /**
         * A background tick. Skipped whenever acting on the result would be disruptive: mid-request,
         * mid-revoke, or with the confirmation open — swapping the list out from under that dialog
         * would change what "revoke this device" refers to while somebody is reading it.
         */
        const refresh = () => {
            if (self.status.isLoading || self.revokeStatus.isLoading || self.deviceToRevoke) {
                return;
            }

            fetchDevices(true);
        };

        /**
         * The page's own initial load. Distinct from applyQuery so the table's mount-time echo
         * can be told apart from somebody actually turning a page.
         */
        const start = () => {
            self.ignoreNextQuery = true;
            load();
        };

        /**
         * Which states the table shows. An empty selection falls back to the live ones rather
         * than showing nothing — an empty filter reads as a broken page, not as a choice.
         */
        const setStatuses = (statuses: string[]) => {
            self.statuses.replace(statuses.length ? statuses : [PAIRING_STATUS_ACTIVE]);
            self.page = 0;
            self.ignoreNextQuery = true;
            load();
        };

        const toggleStatus = (status: string) => {
            const next = self.statuses.includes(status)
                ? self.statuses.filter(s => s !== status)
                : [...self.statuses, status];

            setStatuses(next);
        };

        const setSearchText = (searchText: string) => {
            self.searchText = searchText || '';
            // A narrowed search almost never has as many pages as the old one, so staying on
            // page 4 would show an empty table and read as "no devices".
            self.page = 0;
            self.ignoreNextQuery = true;
            load();
        };

        /**
         * One entry point for both paging and sorting, because the table reports them together:
         * a page change while a sort is active arrives carrying that sort, and treating it as a
         * fresh sort would snap the user back to page one every time they turned the page.
         */
        const applyQuery = (page: number, sortFieldName: string, sortDirection: number) => {
            if (self.ignoreNextQuery) {
                self.ignoreNextQuery = false;

                return;
            }

            const sortChanged = sortFieldName !== self.sortFieldName || sortDirection !== self.sortDirection;

            self.sortFieldName = sortFieldName || '';
            self.sortDirection = sortDirection;
            // A new ordering makes the old page number meaningless — row 30 is a different row now.
            self.page = sortChanged ? 0 : page;

            load();
        };

        const askToRevoke = (id: string) => {
            self.revokeError = '';
            self.deviceToRevoke = id;
        };

        const cancelRevoke = () => {
            self.deviceToRevoke = '';
        };

        const confirmRevoke = flow(function* () {
            const id = self.deviceToRevoke;
            if (!id) {
                return;
            }

            self.deviceToRevoke = '';
            self.revokeError = '';
            self.revokeStatus.setLoading();

            const result = yield performRevokeManagedDevice(api, {
                payload: { id },
                errorHandlers: { DEFAULT: '' },
                disableDefaultErrorHandlers: true,
            });

            if (result.success) {
                self.revokeStatus.setDone();
                // Reloaded rather than spliced out: revoking frees a seat and can change what the
                // current page holds, and the count in the header has to stay honest.
                yield load();
            } else {
                self.revokeError = result.error?.body?.errors?.[0] || '';
                self.revokeStatus.setError();
            }
        });

        const resetState = () => {
            self.searchText = '';
            self.page = 0;
            self.sortFieldName = '';
            self.sortDirection = 1;
            self.deviceToRevoke = '';
            self.revokeError = '';
            self.devices = [];
            self.meta = emptyMeta();
            self.summary = null;
            self.canSeeHistory = false;
            self.statuses.replace([PAIRING_STATUS_ACTIVE]);
            self.ignoreNextQuery = false;
            self.status.setInitial();
            self.revokeStatus.setInitial();
        };

        return ({
            load,
            refresh,
            start,
            setSearchText,
            setStatuses,
            toggleStatus,
            applyQuery,
            askToRevoke,
            cancelRevoke,
            confirmRevoke,
            resetState,
        });
    })
    /**
     * Polling lives in its own actions block so that the timer can call `self.refresh()` — the
     * action as MST wrapped it. The plain `refresh` closure from the block above would run outside
     * any action context, and the flow it starts needs a parent one; that mismatch is invisible
     * until a tick actually fires.
     */
    .actions(self => {
        const stopPolling = () => {
            if (self.pollHandle) {
                clearInterval(self.pollHandle);
                self.pollHandle = undefined;
            }
        };

        const startPolling = () => {
            if (self.pollHandle) {
                return;
            }

            self.pollHandle = setInterval(() => {
                // Nobody is reading a hidden tab. The page refreshes on the way back instead of
                // spending a request a minute in the background.
                if (typeof document !== 'undefined' && document.hidden) {
                    return;
                }

                self.refresh();
            }, MANAGED_DEVICES_POLL_INTERVAL_MS);
        };

        const cleanUp = () => {
            stopPolling();
            self.resetState();
        };

        return ({
            startPolling,
            stopPolling,
            cleanUp,
        });
    })
    .views(self => {
        // A plain function rather than a sibling view, because `self` inside one views block does
        // not yet carry that block's own getters.
        const showingActiveOnly = () =>
            self.statuses.length === 1 && self.statuses[0] === PAIRING_STATUS_ACTIVE;

        return ({
            /**
             * Only once a load has actually come back empty. Before that there is nothing to say,
             * and "nobody has paired a device yet" would be a claim the page has not checked.
             */
            get isEmpty() {
                return !self.devices.length && self.status.isDone;
            },

            /**
             * Whether the table is on its default view. Drives the empty-state wording, which
             * should say "nothing matches that filter" rather than "nobody has paired a device".
             */
            get isShowingActiveOnly() {
                return showingActiveOnly();
            },

            get isFiltered() {
                return !!self.searchText || !showingActiveOnly();
            },

            get revokeTarget() {
                return self.devices.find(device => device.id === self.deviceToRevoke) || null;
            },
        });
    });

type ManagedDevicesUIStoreFactoryType = typeof ManagedDevicesUIStoreInferred;
interface IManagedDevicesUIStoreFactory extends ManagedDevicesUIStoreFactoryType { }
export const ManagedDevicesUIStore: IManagedDevicesUIStoreFactory = ManagedDevicesUIStoreInferred;
export interface IManagedDevicesUIStore extends Instance<IManagedDevicesUIStoreFactory> { }
export interface IManagedDevicesUIStoreSnapshotIn extends SnapshotIn<IManagedDevicesUIStore> { }
